import classnames from "classnames";
import React, {
  Children,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
  isValidElement,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { FormattedMessage } from "react-intl";
import { Button, NavLink } from "reactstrap";
import "./Steps.css";
import Nav from "reactstrap/lib/Nav";
import NavItem from "reactstrap/lib/NavItem";
import * as uuid from "uuid";
import type { IStepProps } from "./Step";
import { InputRendererContext } from "src/shared/forms/formrenderers/InputRenderer";
import { StepsContext } from "src/shared/forms/steps/StepsContext";
import { StepsFooter } from "src/shared/forms/steps/StepsFooter";
import {
  type StepsNavigationReturn,
  useStepsNavigation,
} from "src/shared/forms/steps/useStepsNavigation";

export interface IStepsProps {
  lastStepRender: ReactNode;
  children: Array<ReactElement<IStepProps> | null | undefined | boolean>;
  nextButtonDisabled?: boolean;
  stepsNavigationRef?: MutableRefObject<StepsNavigationReturn | undefined>;
}

export type IStepsFormProps = Omit<IStepsProps, "lastStepRender" | "children">;

export const Steps = ({
  lastStepRender,
  nextButtonDisabled,
  stepsNavigationRef,
  children,
}: IStepsProps) => {
  const onNextClickRef = useRef<() => Promise<void>>(null);
  const stepsContext = useMemo(
    () => ({
      onNextClickRef,
    }),
    [],
  );

  const stepsNavigation = useStepsNavigation(children);
  const {
    currentStepIndex,
    setCurrentStepIndex,
    previousValidStepIndex,
    nextValidStepIndex,
  } = stepsNavigation;
  if (stepsNavigationRef) {
    stepsNavigationRef.current = stepsNavigation;
  }

  const handleGoToStep = useCallback(
    (stepIndex: number): void => {
      setCurrentStepIndex(stepIndex);
    },
    [setCurrentStepIndex],
  );

  const handlePreviousStepClick = useCallback((): void => {
    if (previousValidStepIndex === null) {
      throw new Error("There is no previous step.");
    }
    setCurrentStepIndex(previousValidStepIndex);
  }, [previousValidStepIndex, setCurrentStepIndex]);

  const handleNextStepClick = useCallback(
    async (e: SyntheticEvent) => {
      // Stop leaving page.
      e.preventDefault();
      // Stop propagating to the next form.
      e.stopPropagation();

      try {
        await onNextClickRef.current?.();
      } catch (ex) {
        return;
      }

      if (nextValidStepIndex === null) {
        throw new Error("There is no next step.");
      }

      setCurrentStepIndex(nextValidStepIndex);
    },
    [nextValidStepIndex, setCurrentStepIndex],
  );

  const formID = useMemo(() => {
    return "FORM_" + uuid.v4();
  }, []);

  return (
    <StepsContext.Provider value={stepsContext}>
      <div className="steps">
        <Nav tabs className="steps-nav">
          {Children.map(children, (child, index) => {
            if (!isValidElement(child)) {
              return null;
            }
            const { name } = child.props;
            return (
              <NavItem
                key={index}
                className={classnames({
                  enabled: currentStepIndex >= index,
                  "enabled-last": currentStepIndex === index,
                })}
              >
                <NavLink
                  className={classnames({ active: currentStepIndex === index })}
                  onClick={
                    currentStepIndex > index
                      ? () => handleGoToStep(index)
                      : undefined
                  }
                >
                  {name}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>
        {/* Not the last step */}
        {nextValidStepIndex !== null ? (
          <>
            {/* Create Form on <body> */}
            {createPortal(
              <form id={formID} onSubmit={handleNextStepClick} />,
              document.body,
            )}

            {/* Direct children to form */}
            <InputRendererContext.Provider value={{ form: formID }}>
              {children[currentStepIndex]}
            </InputRendererContext.Provider>

            <StepsFooter
              isFirstStep={previousValidStepIndex === null}
              onPreviousStepClick={handlePreviousStepClick}
            >
              <p>
                <Button
                  color="primary"
                  disabled={nextButtonDisabled}
                  type="submit"
                  form={formID}
                >
                  <FormattedMessage id="steps.next" />
                </Button>
              </p>
            </StepsFooter>
          </>
        ) : (
          <>
            {children[currentStepIndex]}

            <StepsFooter
              isFirstStep={previousValidStepIndex === null}
              onPreviousStepClick={handlePreviousStepClick}
            >
              {lastStepRender}
            </StepsFooter>
          </>
        )}
      </div>
    </StepsContext.Provider>
  );
};

/**
 * @deprecated
 */
export default Steps;
